import $ from 'jquery';

import {asyncThrottle} from 'BenchlingUtil';
import * as EventConstants from 'constants/EventConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import AppDispatcher from 'lib/Dispatcher';
import * as NoteLinkedFileDAO from 'notebook/notes/attachments/NoteLinkedFileDAO';
import {CreateLocalSyncCopyResponse} from 'notebook/notes/attachments/NoteLinkedFileDAO';
import TFIDAO from 'notebook/tfi/TFIDAO';
import {NoteLinkedFile, NoteLinkedObject} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import {REQUEST_TOOK_TOO_LONG} from 'util/TaskRunner';

import TFIConstants, {TFIAccount, TFIFileMeta, TFRevsByTFIMetaId} from './TFIConstants';

const TFIActions = {
  addAccount(account: TFIAccount) {
    AppDispatcher.dispatch({
      actionType: EventConstants.TFI_ACCOUNT_ADDED,
      account,
    });
  },

  deleteAccount(accountId: string) {
    TFIDAO.deleteAccount(accountId)
      .done(() => {
        notifications.success('Account unlinked');
        AppDispatcher.dispatch({
          actionType: EventConstants.TFI_ACCOUNT_DELETED,
          accountId,
        });
      })
      .fail(notifications.notifyError);
  },

  listFolder(accountId: string, folderId: string) {
    AppDispatcher.dispatch({
      actionType: EventConstants.TFI_FOLDER_LISTED,
      folderId,
    });

    // If folderId and accountId are the same, we have the root folder
    const serverFolderId = folderId === accountId ? null : folderId;
    TFIDAO.listFolder(accountId, serverFolderId)
      .done((response) => {
        if (response?.warning_message) {
          notifications.warn(response.warning_message);
        }
        AppDispatcher.dispatch({
          actionType: EventConstants.TFI_FOLDER_CONTENTS_FETCHED,
          tfiObjects: response?.results,
          folderId,
        });
      })
      .fail(notifications.notifyError);
  },

  fetchAccounts() {
    TFIDAO.fetchAccounts().done((response) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.TFI_ACCOUNTS_FETCHED,
        accounts: response?.data,
      });
    });
  },

  importFile({
    accountId,
    fileId,
    filename,
    folderId,
    importFileType,
  }: {
    accountId: string;
    fileId: string;
    filename: string;
    folderId: string;
    importFileType: unknown;
  }) {
    LoadingScreen.show();
    TFIDAO.importFile({accountId, fileId, fileName: filename})
      .fail(notifications.notifyError)
      .done(() => {
        FolderDAOCache.invalidateFolderContents(folderId);
        notifications.success(`Successfully imported ${filename}`);
      })
      .always(() => {
        LoadingScreen.hide();
      });
  },

  createLocalSyncCopy(
    noteLinkedObjectId: string,
    {onSuccess}: {onSuccess: (tfiFileMeta: TFIFileMeta) => void}
  ) {
    LoadingScreen.show('Preparing for edit...');
    NoteLinkedFileDAO.createLocalSyncCopy({uuid: noteLinkedObjectId})
      .fail(notifications.notifyError)
      .done(({tfiFileMeta}: CreateLocalSyncCopyResponse) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TFI_BULK_REGISTER_REMOTE_REVISION_ID,
          thirdpartyFileRevsByTFIMetaId: {
            [tfiFileMeta.id]: tfiFileMeta.thirdpartyFileRev,
          },
        });
        onSuccess(tfiFileMeta);
      })
      .always(() => {
        LoadingScreen.hide();
      });
  },

  syncFile(
    tfiFileMeta: TFIFileMeta,
    fileName: string,
    {onSuccess}: {onSuccess: (noteLinkedObject: NoteLinkedObject) => void}
  ) {
    LoadingScreen.show('Syncing...');
    AppDispatcher.dispatch({
      actionType: EventConstants.TFI_FILE_META_CHECK_FOR_UPDATES,
      tfiFileMetaId: tfiFileMeta.id,
    });
    TFIDAO.importFile({
      accountId: tfiFileMeta.tfiAccount.id,
      fileId: tfiFileMeta.thirdpartyFileId,
      fileName,
    })
      // Hide the loading screen first, so that onSuccess can trigger its own loading screen
      .always(() => {
        LoadingScreen.hide();
      })
      .then(({noteLinkedObject}: {noteLinkedObject: NoteLinkedFile}) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TFI_BULK_REGISTER_REMOTE_REVISION_ID,
          thirdpartyFileRevsByTFIMetaId: {
            [noteLinkedObject.tfiFileMeta?.id!]: noteLinkedObject.tfiFileMeta?.thirdpartyFileRev,
          },
        });
        onSuccess(noteLinkedObject);
      })
      .fail(notifications.notifyError)
      .always(() => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TFI_FILE_META_CHECK_FOR_UPDATES_COMPLETED,
          tfiFileMetaId: tfiFileMeta.id,
        });
      });
  },

  _bulkGetUpstreamThirdpartyFileRev(tfiFileMetaIds: Array<number>) {
    return TFIDAO.bulkGetUpstreamThirdpartyFileRev({tfiFileMetaIds}).then(
      ({thirdpartyFileRevsByTFIMetaId}: TFRevsByTFIMetaId) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TFI_BULK_REGISTER_REMOTE_REVISION_ID,
          thirdpartyFileRevsByTFIMetaId,
        });
        return thirdpartyFileRevsByTFIMetaId;
      }
    );
  },

  updateUpstreamThirdpartyFileRev(tfiFileMetaId: number) {
    AppDispatcher.dispatch({
      actionType: EventConstants.TFI_FILE_META_CHECK_FOR_UPDATES,
      tfiFileMetaId,
    });
    return TFIActions._bulkGetUpstreamThirdpartyFileRev([tfiFileMetaId])
      .then(
        (thirdpartyFileRevsByTFIMetaId: TFRevsByTFIMetaId['thirdpartyFileRevsByTFIMetaId']) =>
          thirdpartyFileRevsByTFIMetaId[tfiFileMetaId]
      )
      .always(() => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TFI_FILE_META_CHECK_FOR_UPDATES_COMPLETED,
          tfiFileMetaId,
        });
      })
      .fail((e) => {
        if (e.userMessage === REQUEST_TOOK_TOO_LONG) {
          notifications.error(TFIConstants.SYNCING_TIMEOUT_MESSAGE);
        } else {
          notifications.notifyError(e);
        }
      });
  },

  registerCheckForUpdates(tfiFileMetaId: number) {
    AppDispatcher.dispatch({
      actionType: EventConstants.TFI_FILE_META_REGISTER_CHECK_FOR_UPDATES,
      tfiFileMetaId,
    });
  },

  deregisterCheckForUpdates(tfiFileMetaId: number) {
    AppDispatcher.dispatch({
      actionType: EventConstants.TFI_FILE_META_DEREGISTER_CHECK_FOR_UPDATES,
      tfiFileMetaId,
    });
  },

  bulkCheckForUpdates(getTFIFileMetaIds: () => Array<number>) {
    asyncThrottle('TFIActions.bulkCheckForUpdates', () => {
      const tfiFileMetaIds = getTFIFileMetaIds();
      if (tfiFileMetaIds.length === 0) {
        return $.Deferred().resolve().promise();
      }
      return TFIActions._bulkGetUpstreamThirdpartyFileRev(tfiFileMetaIds).fail((e) => {
        if (e.userMessage === REQUEST_TOOK_TOO_LONG) {
          notifications.warn(TFIConstants.SYNCING_TIMEOUT_MESSAGE);
        }
      });
    });
  },

  toggleTFIAddModal(show: boolean) {
    AppDispatcher.dispatch({
      actionType: EventConstants.TFI_TOGGLE_ADD_MODAL,
      show,
    });
  },

  listAccounts() {
    AppDispatcher.dispatch({
      actionType: EventConstants.TFI_LIST_ACCOUNTS,
    });
  },

  setQuery(query: string) {
    AppDispatcher.dispatch({
      actionType: EventConstants.TFI_SEARCH_SET_QUERY,
      query,
    });
  },

  clearSearch() {
    AppDispatcher.dispatch({
      actionType: EventConstants.TFI_SEARCH_CLEAR_SEARCH,
    });
  },

  setTFIAccountFilter(tfiAccountFilter: string | null) {
    AppDispatcher.dispatch({
      actionType: EventConstants.TFI_SEARCH_SET_TFI_ACCOUNT_FILTER,
      tfiAccountFilter,
    });
  },
};

export default TFIActions;
